/*!
 * Flooritize freight engine (FL 2.0 1.4 candidate) — added 2026-10-13.
 * Bungii-inspired shipment-class schedule. Pure calculation: no network, no DOM, no checkout effect.
 *
 * STATUS: NOT CONNECTED TO THE CART OR CHECKOUT. Shopify checkout charges come only from the store's
 * delivery-profile rates. This file is the single source of truth for the schedule, so a future
 * approved checkout-rate mechanism and any cart estimate can use the same numbers.
 *
 * Anything not approved returns { status: "quote" } with reasons — never an invented price.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FLZFreight = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ===================== CONFIG — the only place to edit rates/rules ===================== */
  var CONFIG = {
    version: '2026-10-13',
    currency: 'USD',
    band_miles: 10,          // 0–10 = first band; each further 10-mile band adds band_increment
    banded_through_miles: 100,
    max_auto_miles: 250,     // beyond this: quote
    max_auto_pallets: 2,     // more pallets: quote
    loose_max_lb: 500,       // F1 ceiling
    mile_rounding: 'none',   // per-mile charge beyond 100 mi uses exact miles; rounding policy not set
    classes: {
      F1: { label: 'Loose shipment, up to 500 lb',  base: 79,  band_increment: 10, per_mile_beyond: 2.50 },
      F2: { label: 'One pallet, hand-unload',       base: 89,  band_increment: 10, per_mile_beyond: 2.50 },
      F3: { label: 'Two pallets, hand-unload',      base: 178, band_increment: 20, per_mile_beyond: 4.00 },
      F4: { label: 'One pallet, white-glove',       base: 159, band_increment: 10, per_mile_beyond: null }, // unresolved
      F5: { label: 'Two pallets, white-glove',      base: 318, band_increment: 20, per_mile_beyond: null }  // unresolved
    },
    // Distance measurement is NOT approved. Straight-line x 1.2 is provisional only and disabled.
    distance: { approved: false, method: null, provisional_method: 'straight_line_x_1.2 (NOT APPROVED)' },
    // Vendor ship-from origins. Each must be verified before a vendor shipment can be priced.
    vendors: {
      daltile:    { origin: null, origin_verified: false, freight_enabled: true },
      marazzi:    { origin: null, origin_verified: false, freight_enabled: true },
      msi:        { origin: null, origin_verified: false, freight_enabled: true },
      bedrosians: { origin: null, origin_verified: false, freight_enabled: true },
      ecore:      { origin: null, origin_verified: false, freight_enabled: false } // Ecore freight deferred
    }
    // Excluded from the core formula: signature, refused-delivery, premium-market and optional accessorial fees.
  };
  /* ======================================================================================= */

  function round2(n) { return Math.round(n * 100) / 100; }
  function norm(v) { return String(v || '').trim().toLowerCase(); }

  /** Shipment class from explicit facts. Unknown or uncovered combinations return null (quote). */
  function classify(s, cfg) {
    cfg = cfg || CONFIG;
    var reasons = [];
    var pallets = s.pallets, handling = norm(s.handling), lb = s.weightLb;
    if (pallets === undefined || pallets === null || pallets === '') reasons.push('pallet_count_unknown');
    else if (!(Number.isInteger(pallets) && pallets >= 0)) reasons.push('pallet_count_invalid');
    if (handling !== 'hand' && handling !== 'white_glove') reasons.push('handling_unknown');
    if (reasons.length) return { cls: null, reasons: reasons };
    if (pallets > cfg.max_auto_pallets) return { cls: null, reasons: ['more_than_' + cfg.max_auto_pallets + '_pallets'] };
    if (pallets === 0) {
      if (handling !== 'hand') return { cls: null, reasons: ['loose_white_glove_not_in_schedule'] };
      if (typeof lb !== 'number' || !(lb > 0)) return { cls: null, reasons: ['weight_unknown'] };
      if (lb > cfg.loose_max_lb) return { cls: null, reasons: ['loose_over_' + cfg.loose_max_lb + '_lb'] };
      return { cls: 'F1', reasons: [] };
    }
    if (handling === 'hand') return { cls: pallets === 1 ? 'F2' : 'F3', reasons: [] };
    return { cls: pallets === 1 ? 'F4' : 'F5', reasons: [] };
  }

  /** Price for a known class and distance. */
  function priceClass(cls, miles, cfg) {
    cfg = cfg || CONFIG;
    var c = cfg.classes[cls];
    if (!c) return { status: 'quote', reasons: ['unknown_class'] };
    if (typeof miles !== 'number' || !isFinite(miles) || miles < 0) return { status: 'quote', reasons: ['distance_unknown'] };
    if (miles > cfg.max_auto_miles) return { status: 'quote', reasons: ['over_' + cfg.max_auto_miles + '_miles'] };
    var lastBand = Math.ceil(cfg.banded_through_miles / cfg.band_miles) - 1; // 9
    if (miles <= cfg.banded_through_miles) {
      var band = miles <= cfg.band_miles ? 0 : Math.ceil(miles / cfg.band_miles) - 1;
      return { status: 'priced', cls: cls, amount: round2(c.base + band * c.band_increment) };
    }
    if (c.per_mile_beyond === null || c.per_mile_beyond === undefined) {
      return { status: 'quote', cls: cls, reasons: ['long_distance_rate_unresolved_' + cls] };
    }
    var at100 = c.base + lastBand * c.band_increment;
    return { status: 'priced', cls: cls, amount: round2(at100 + (miles - cfg.banded_through_miles) * c.per_mile_beyond) };
  }

  /** Distance is only accepted when the measurement rule and the vendor origin are approved/verified. */
  function resolveDistance(s, cfg) {
    cfg = cfg || CONFIG;
    var v = cfg.vendors[norm(s.vendor)];
    if (!v) return { miles: null, reasons: ['vendor_not_configured'] };
    var reasons = [];
    if (!v.freight_enabled) reasons.push('vendor_freight_deferred');
    if (!v.origin_verified || !v.origin) reasons.push('vendor_origin_unverified');
    if (!cfg.distance.approved) reasons.push('distance_rule_unapproved');
    if (typeof s.distanceMiles !== 'number') reasons.push('distance_unknown');
    return reasons.length ? { miles: null, reasons: reasons } : { miles: s.distanceMiles, reasons: [] };
  }

  /** One vendor shipment: { vendor, pallets, handling: 'hand'|'white_glove', weightLb, distanceMiles } */
  function quoteShipment(s, cfg) {
    cfg = cfg || CONFIG;
    var c = classify(s, cfg), d = resolveDistance(s, cfg);
    var reasons = c.reasons.concat(d.reasons);
    if (reasons.length) return { vendor: norm(s.vendor), status: 'quote', cls: c.cls, reasons: reasons };
    var p = priceClass(c.cls, d.miles, cfg);
    p.vendor = norm(s.vendor);
    return p;
  }

  /**
   * Whole order: one shipment per vendor. Duplicate vendor entries are rejected (quote) rather than
   * charged twice. Total is returned only when every shipment is priced.
   */
  function quoteOrder(shipments, cfg) {
    cfg = cfg || CONFIG;
    var seen = {}, results = [], dup = false;
    (shipments || []).forEach(function (s) {
      var k = norm(s.vendor);
      if (seen[k]) { dup = true; return; }
      seen[k] = true;
      results.push(quoteShipment(s, cfg));
    });
    if (!results.length) return { status: 'quote', reasons: ['no_shipments'], shipments: [] };
    if (dup) return { status: 'quote', reasons: ['duplicate_vendor_shipment'], shipments: results };
    var allPriced = results.every(function (r) { return r.status === 'priced'; });
    if (!allPriced) return { status: 'quote', reasons: ['one_or_more_shipments_need_quote'], shipments: results };
    var total = round2(results.reduce(function (a, r) { return a + r.amount; }, 0));
    return { status: 'priced', total: total, currency: cfg.currency, shipments: results };
  }

  return { CONFIG: CONFIG, classify: classify, priceClass: priceClass, resolveDistance: resolveDistance,
           quoteShipment: quoteShipment, quoteOrder: quoteOrder, connectedToCheckout: false };
});
